import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import moment from 'moment-timezone';
import { Btn, Tag, Card, Avatar, QGlyph, Icons, Sidebar, MobileNav, LoadingScreen, useToast } from './ui';
import { TranslateText } from './TranslateText';
import { RightSidebar, UserInfo } from './mypage';

const APP_URL = process.env.REACT_APP_APP_URL || window.location.origin;

// ─── Types ───────────────────────────────────────────────────────────────────

interface CommunityQuestion {
  id: number;
  user_id: string;
  server: string;
  display_name: string;
  avatar: string;
  question: string;
  nsfw: boolean;
  is_sensitive: boolean;
  answer_count: number;
  created_at: string;
}

interface CommunityAnswer {
  id: number;
  question_id: number;
  user_id: string;
  server: string;
  display_name: string;
  avatar: string;
  answer: string;
  nsfw: boolean;
  created_at: string;
}

type Visibility = 'public' | 'unlisted' | 'private';

const UTCtoJST = (d: string) =>
  moment.utc(d).tz('Asia/Tokyo').format('YYYY/MM/DD HH:mm');

const charMax = 500;

// ─── Question Detail Modal ──────────────────────────────────────────────────

const QuestionDetailModal: React.FC<{
  question: CommunityQuestion;
  answers: CommunityAnswer[];
  selfUser: UserInfo | null;
  onClose: () => void;
  onAnswered: (q: CommunityQuestion, answers: CommunityAnswer[]) => void;
  onDeleteQuestion: (id: number) => void;
  onDeleteAnswer: (qId: number, aId: number) => void;
  onReportQuestion: (id: number) => void;
  onReportAnswer: (id: number) => void;
  onPromote: (id: number) => void;
  onShare: (id: number) => void;
}> = ({ question, answers, selfUser, onClose, onAnswered, onDeleteQuestion, onDeleteAnswer, onReportQuestion, onReportAnswer, onPromote, onShare }) => {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [visibility, setVisibility] = useState<Visibility>('public');
  const [nsfw, setNsfw] = useState(false);
  const [postToFediverse, setPostToFediverse] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const isMine = selfUser && question.user_id === selfUser.user_id && question.server === selfUser.server;
  const { t } = useTranslation();

  const handleAnswer = async () => {
    if (!text.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/v1/community/questions/${question.id}/answers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answer: text, nsfw, visibility, post_to_fediverse: postToFediverse }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || t('community.detailAnswerFailed'));
        return;
      }
      const newAnswer = await res.json();
      onAnswered(question, [...answers, newAnswer]);
      setText('');
      setFormOpen(false);
    } catch {
      setError(t('community.detailAnswerFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-2xl bg-white dark:bg-ink-900 rounded-3xl shadow-pop overflow-hidden flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-150 dark:border-ink-800 shrink-0">
          <div className="flex items-center gap-2">
            <QGlyph tone="green" size={28} />
            <span className="font-bold">{t('community.detailTitle')}</span>
            {question.nsfw && <Tag tone="outline" className="!text-rose-500 !border-rose-200">{t('common.nsfw')}</Tag>}
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-ink-100 dark:hover:bg-ink-850 flex items-center justify-center">
            ✕
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1">
          {/* Question */}
          <div className="px-6 py-5 bg-ink-50/60 dark:bg-ink-950/30 border-b border-ink-150 dark:border-ink-800">
            <div className="flex items-center gap-3 mb-3">
              <button
                onClick={() => navigate(`/questionbox/@${question.user_id}@${question.server}`)}
                className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity text-left"
              >
                <Avatar src={question.avatar} seed={question.display_name} size={36} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[14px]">{question.display_name}</div>
                  <div className="text-[12px] text-ink-500 dark:text-ink-400 font-mono truncate">
                    @{question.user_id}@{question.server}
                  </div>
                </div>
              </button>
              <span className="text-[12px] text-ink-400 shrink-0">{UTCtoJST(question.created_at)}</span>
            </div>
            <p className="text-[16px] font-semibold leading-relaxed"><TranslateText text={question.question} inline /></p>
            <div className="flex items-center gap-3 mt-3">
              <span className="text-[12px] text-ink-500 dark:text-ink-400 flex items-center gap-1">
                <Icons.Reply width={12} height={12} /> {t('common.countAnswers', { count: question.answer_count })}
              </span>
              <div className="flex items-center gap-1 ml-auto">
                {isMine && (
                  <>
                    <Btn variant="softGreen" size="sm" onClick={() => onPromote(question.id)}>
                      <Icons.Share width={12} height={12} /> {t('common.promote')}
                    </Btn>
                    <Btn variant="danger" size="sm" onClick={() => onDeleteQuestion(question.id)}>
                      <Icons.Trash width={12} height={12} /> {t('common.delete')}
                    </Btn>
                  </>
                )}
                {!isMine && (
                  <>
                    {selfUser && (
                      <Btn variant="softGreen" size="sm" onClick={() => onShare(question.id)}>
                        <Icons.Share width={12} height={12} /> {t('common.share')}
                      </Btn>
                    )}
                    <Btn variant="ghost" size="sm" onClick={() => onReportQuestion(question.id)}>
                      <Icons.Flag width={12} height={12} /> {t('common.report')}
                    </Btn>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Answers */}
          <div className="px-6 py-4 space-y-4">
            {answers.length === 0 ? (
              <p className="text-center text-ink-400 text-[13px] py-6">{t('community.detailEmpty')}</p>
            ) : (
              answers.map((a) => {
                const answerIsMine = selfUser && a.user_id === selfUser.user_id && a.server === selfUser.server;
                return (
                  <Card key={a.id} className="!p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        onClick={() => navigate(`/questionbox/@${a.user_id}@${a.server}`)}
                        className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-80 transition-opacity text-left"
                      >
                        <Avatar src={a.avatar} seed={a.display_name} size={24} />
                        <div className="flex-1 min-w-0">
                          <span className="text-[13px] font-semibold">{a.display_name}</span>
                          <span className="text-[11px] text-ink-500 dark:text-ink-400 font-mono ml-2 truncate">
                            @{a.user_id}@{a.server}
                          </span>
                        </div>
                      </button>
                      <span className="text-[11px] text-ink-400 shrink-0">{UTCtoJST(a.created_at)}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <QGlyph tone="blue" size={22} />
                      <p className="text-[14px] leading-relaxed flex-1"><TranslateText text={a.answer} inline /></p>
                    </div>
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-ink-150 dark:border-ink-800">
                      {answerIsMine && (
                        <Btn variant="ghost" size="sm" onClick={() => onDeleteAnswer(question.id, a.id)}>
                          <Icons.Trash width={12} height={12} /> {t('common.delete')}
                        </Btn>
                      )}
                      {!answerIsMine && (
                        <Btn variant="ghost" size="sm" onClick={() => onReportAnswer(a.id)}>
                          <Icons.Flag width={12} height={12} /> {t('common.report')}
                        </Btn>
                      )}
                    </div>
                  </Card>
                );
              })
            )}
          </div>
        </div>

        {/* Answer form (fixed bottom) */}
        {!selfUser && (
          <div className="px-6 py-4 border-t border-ink-150 dark:border-ink-800 shrink-0 text-center">
            <p className="text-[13px] text-ink-500 dark:text-ink-400 mb-3">{t('common.requiredForAnswer')}</p>
            <Btn variant="primary" size="md" onClick={() => window.location.href = '/login'}>
              {t('common.loginAndAnswer')}
            </Btn>
          </div>
        )}
        {selfUser && (
          <div className="px-6 py-4 border-t border-ink-150 dark:border-ink-800 shrink-0">
            {!formOpen ? (
              <Btn variant="primary" size="md" className="w-full" onClick={() => setFormOpen(true)}>
                <Icons.Reply width={14} height={14} /> {t('common.answerVerb')}
              </Btn>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-3 text-[12px] text-ink-500 dark:text-ink-400">
                  <Avatar src={selfUser.profile_img} seed={selfUser.display_name} size={22} />
                  <span className="font-semibold">{selfUser.display_name}</span>
                  <span className="font-mono">@{selfUser.user_id}@{selfUser.server}</span>
                </div>
                <textarea
                  rows={3}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={t('community.detailPlaceholder')}
                  autoFocus
                  className="w-full bg-ink-100 dark:bg-ink-850 rounded-2xl p-3 text-[14px] leading-relaxed resize-none outline-none placeholder:text-ink-400 focus:ring-2 focus:ring-brand-blue-300 transition"
                />
                <div className="flex justify-between items-center mt-1">
                  <span className={`text-[11px] ${text.length > charMax ? 'text-rose-500' : 'text-ink-400'}`}>
                    {text.length} / {charMax}
                  </span>
                  {error && <span className="text-[11px] text-rose-500">{error}</span>}
                </div>
                <div className="flex items-center gap-4 mt-3 flex-wrap">
                  <div className="flex items-center gap-1 p-1 rounded-full bg-ink-100 dark:bg-ink-850 border border-ink-150 dark:border-ink-800">
                    {([
                      { v: 'public' as Visibility, l: t('common.public'), i: Icons.Globe },
                      { v: 'unlisted' as Visibility, l: t('common.unlisted'), i: Icons.Compass },
                      { v: 'private' as Visibility, l: t('common.follower'), i: Icons.Lock },
                    ] as { v: Visibility; l: string; i: React.FC<any> }[]).map((o) => {
                      const on = visibility === o.v;
                      return (
                        <button
                          key={o.v}
                          onClick={() => setVisibility(o.v)}
                          className={`inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[11.5px] font-medium transition ${
                            on
                              ? 'bg-brand-blue-50 text-brand-blue-700 dark:bg-brand-blue-900/40 dark:text-brand-blue-100'
                              : 'text-ink-500 hover:text-ink-800 dark:hover:text-ink-100'
                          }`}
                        >
                          <o.i width={12} height={12} /> {o.l}
                        </button>
                      );
                    })}
                  </div>
                  <label className="flex items-center gap-1.5 text-[12px] cursor-pointer">
                    <input type="checkbox" checked={nsfw} onChange={(e) => setNsfw(e.target.checked)} className="rounded" />
                    {t('common.nsfw')}
                  </label>
                  <label className="flex items-center gap-1.5 text-[12px] cursor-pointer">
                    <input type="checkbox" checked={postToFediverse} onChange={(e) => setPostToFediverse(e.target.checked)} className="rounded" />
                    {t('community.detailPostToFediverse')}
                  </label>
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  <Btn variant="ghost" size="md" onClick={() => { setFormOpen(false); setText(''); setError(''); }}>
                    {t('common.cancel')}
                  </Btn>
                  <Btn
                    variant="primary"
                    size="md"
                    onClick={handleAnswer}
                    disabled={!text.trim() || loading || text.length > charMax}
                  >
                    <Icons.Sparkle width={14} height={14} />
                    {loading ? t('common.posting') : t('community.detailPostAnswer')}
                  </Btn>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Fediverse Promote Modal ──────────────────────────────────────────────────

const FediversePromoteModal: React.FC<{
  question: CommunityQuestion;
  selfUser: UserInfo;
  onClose: () => void;
  onPosted: () => void;
  mode?: 'promote' | 'share';
}> = ({ question, selfUser, onClose, onPosted, mode = 'promote' }) => {
  const { t } = useTranslation();
  const link = `${APP_URL}/community/questions/${question.id}`;
  const isShare = mode === 'share';
  const [text, setText] = useState(
    isShare
      ? t('community.shareDefault', { question: question.question, link })
      : t('community.promoteDefault', { question: question.question, link })
  );
  const [visibility, setVisibility] = useState<Visibility>('public');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const charLimit = 500;
  const endpoint = isShare
    ? `/api/v1/community/questions/${question.id}/share`
    : `/api/v1/community/questions/${question.id}/promote`;
  const modalTitle = isShare ? t('community.shareTitle') : t('community.promoteTitle');
  const submitLabel = isShare ? t('common.shareVerb') : t('common.promote');

  const handlePost = async () => {
    if (!text.trim() || loading) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, visibility }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || t('community.postFailed'));
        return;
      }
      onPosted();
      onClose();
    } catch {
      setError(t('community.postFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <Card className="w-full max-w-lg !p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-[16px] flex items-center gap-2">
            <Icons.Share width={18} height={18} className="text-brand-green-500" />
            {modalTitle}
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-ink-100 dark:hover:bg-ink-850 flex items-center justify-center">
            ✕
          </button>
        </div>

        <div className="mb-3 flex items-center gap-2 text-[12px] text-ink-500 dark:text-ink-400">
          <Avatar src={selfUser.profile_img} seed={selfUser.display_name} size={20} />
          <span className="font-mono">@{selfUser.user_id}@{selfUser.server}</span>
          <span>{t('community.postAs')}</span>
        </div>

        <textarea
          rows={6}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="w-full bg-ink-100 dark:bg-ink-850 rounded-2xl p-4 text-[14px] leading-relaxed resize-none outline-none placeholder:text-ink-400 focus:ring-2 focus:ring-brand-green-300 transition mb-1"
        />
        <div className="flex justify-between items-center mb-4">
          <span className={`text-[11px] ${text.length > charLimit ? 'text-rose-500' : 'text-ink-400'}`}>
            {text.length} / {charLimit}
          </span>
          <a href={link} target="_blank" rel="noopener noreferrer"
            className="text-[11px] text-brand-blue-500 hover:underline truncate max-w-[60%]">
            {link}
          </a>
        </div>

        <div className="flex items-center gap-1 p-1 rounded-full bg-ink-100 dark:bg-ink-850 border border-ink-150 dark:border-ink-800 w-fit mb-4">
          {([
            { v: 'public' as Visibility, l: t('common.public'), i: Icons.Globe },
            { v: 'unlisted' as Visibility, l: t('common.unlisted'), i: Icons.Compass },
            { v: 'private' as Visibility, l: t('common.follower'), i: Icons.Lock },
          ] as { v: Visibility; l: string; i: React.FC<React.SVGProps<SVGSVGElement>> }[]).map((o) => {
            const on = visibility === o.v;
            return (
              <button
                key={o.v}
                onClick={() => setVisibility(o.v)}
                className={`inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[11.5px] font-medium transition ${
                  on
                    ? 'bg-brand-blue-50 text-brand-blue-700 dark:bg-brand-blue-900/40 dark:text-brand-blue-100'
                    : 'text-ink-500 hover:text-ink-800 dark:hover:text-ink-100'
                }`}
              >
                <o.i width={12} height={12} /> {o.l}
              </button>
            );
          })}
        </div>

        {error && <p className="text-[12px] text-rose-500 mb-3">{error}</p>}

        <div className="flex gap-2 justify-end">
          <Btn variant="ghost" size="md" onClick={onClose}>{t('common.cancel')}</Btn>
          <Btn
            variant="green"
            size="md"
            onClick={handlePost}
            disabled={!text.trim() || loading || text.length > charLimit}
          >
            <Icons.Share width={14} height={14} />
            {loading ? t('common.posting') : submitLabel}
          </Btn>
        </div>
      </Card>
    </div>
  );
};

// ─── Report Modal ─────────────────────────────────────────────────────────────

const ReportModal: React.FC<{
  onClose: () => void;
}> = ({ onClose }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { t } = useTranslation();

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    setLoading(true);
    try {
      // This is a placeholder; the actual endpoint is called from the parent
      setDone(true);
      setTimeout(onClose, 1500);
    } catch {
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <Card className="w-full max-w-md !p-6">
        <h3 className="font-bold text-lg mb-1 flex items-center gap-2">
          <Icons.Flag width={18} height={18} className="text-rose-500" /> {t('common.report')}
        </h3>
        <p className="text-[13px] text-ink-500 dark:text-ink-400 mb-4">
          {t('community.reportModalDesc')}
        </p>
        {done ? (
          <p className="text-brand-green-600 font-semibold text-center py-4">{t('common.reported')}</p>
        ) : (
          <>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('mypage.reportPlaceholder')}
              className="w-full bg-ink-100 dark:bg-ink-850 rounded-xl p-3 text-[14px] resize-none outline-none placeholder:text-ink-400 mb-4"
            />
            <div className="flex gap-2 justify-end">
              <Btn variant="ghost" size="md" onClick={onClose}>{t('common.cancel')}</Btn>
              <Btn
                variant="danger"
                size="md"
                onClick={handleSubmit}
                disabled={!reason.trim() || loading}
              >
                <Icons.Flag width={14} height={14} /> {t('common.reportVerb')}
              </Btn>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

// ─── Community Page ──────────────────────────────────────────────────────────

const CommunityPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const { show: flash, ToastEl } = useToast();
  const { t } = useTranslation();

  const [selfUser, setSelfUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [unansweredCount, setUnansweredCount] = useState(0);
  const [questions, setQuestions] = useState<CommunityQuestion[]>([]);
  const [page, setPage] = useState(0);
  const [fetching, setFetching] = useState(true);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [detailQ, setDetailQ] = useState<CommunityQuestion | null>(null);
  const [detailAnswers, setDetailAnswers] = useState<CommunityAnswer[]>([]);
  const [, setFetchingDetail] = useState(false);
  const [reportTarget, setReportTarget] = useState<{ type: 'question' | 'answer'; id: number } | null>(null);
  const [promoteTarget, setPromoteTarget] = useState<CommunityQuestion | null>(null);
  const [shareTarget, setShareTarget] = useState<CommunityQuestion | null>(null);
  const [hideSensitive, setHideSensitive] = useState(true);

  const loggedIn = !!Cookies.get('jwtToken');

  // Load self user (optional — guests can view without login)
  useEffect(() => {
    if (!loggedIn) {
      setLoading(false);
      return;
    }
    fetch('/api/v1/profile')
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setSelfUser(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loggedIn) return;
    fetch('/api/v1/count/questions')
      .then((r) => r.ok ? r.json() : { count: 0 })
      .then((d) => setUnansweredCount(d.count))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load questions
  const fetchQuestions = useCallback(async (p: number) => {
    setFetching(true);
    try {
      const res = await fetch(`/api/v1/community/questions?page=${p}`);
      if (res.ok) {
        const data = await res.json();
        setQuestions(data);
      }
    } catch {}
    setFetching(false);
  }, []);

  useEffect(() => {
    fetchQuestions(0);
  }, [fetchQuestions]);

  // Auto-open detail modal from /community/questions/:id or ?q= URL param
  useEffect(() => {
    const qId = routeId ?? searchParams.get('q');
    if (!qId) return;
    fetch(`/api/v1/community/questions/${qId}`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (!data) return;
        const q: CommunityQuestion = {
          id: data.id,
          user_id: data.user_id,
          server: data.server,
          display_name: data.display_name,
          avatar: data.avatar,
          question: data.question,
          nsfw: data.nsfw ?? false,
          is_sensitive: data.is_sensitive ?? false,
          answer_count: data.answers?.length ?? 0,
          created_at: data.created_at,
        };
        setDetailQ(q);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load question detail — depend only on id to avoid infinite loop from setDetailQ inside
  useEffect(() => {
    const id = detailQ?.id;
    if (!id) return;
    let cancelled = false;
    setFetchingDetail(true);
    fetch(`/api/v1/community/questions/${id}`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (cancelled || !data) return;
        setDetailAnswers(data.answers || []);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setFetchingDetail(false); });
    return () => { cancelled = true; };
  }, [detailQ?.id]);

  const handlePostQuestion = async () => {
    if (!text.trim() || posting) return;
    setPosting(true);
    try {
      const res = await fetch('/api/v1/community/questions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: text, nsfw: false }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        flash(d.detail || t('community.postFailed'));
        return;
      }
      flash(t('community.postDone'));
      setText('');
      fetchQuestions(0);
    } catch {
      flash(t('community.postFailed'));
    } finally {
      setPosting(false);
    }
  };

  const handleDeleteQuestion = async (id: number) => {
    if (!window.confirm(t('community.deleteQuestionConfirm'))) return;
    try {
      await fetch(`/api/v1/community/questions/${id}`, { method: 'DELETE' });
      flash(t('community.deleteDone'));
      setDetailQ(null);
      fetchQuestions(page);
    } catch {
      flash(t('community.deleteFailed'));
    }
  };

  const handlePromote = (id: number) => {
    const q = questions.find((q) => q.id === id) ?? detailQ ?? null;
    if (q) setPromoteTarget(q);
  };

  const handleShare = (id: number) => {
    const q = questions.find((q) => q.id === id) ?? detailQ ?? null;
    if (q) setShareTarget(q);
  };

  const handleDeleteAnswer = async (qId: number, aId: number) => {
    if (!window.confirm(t('community.deleteAnswerConfirm'))) return;
    try {
      await fetch(`/api/v1/community/answers/${aId}`, { method: 'DELETE' });
      flash(t('community.deleteDone'));
      setDetailAnswers((prev) => prev.filter((a) => a.id !== aId));
    } catch {
      flash(t('community.deleteFailed'));
    }
  };

  const handleReport = async () => {
    if (!reportTarget) return;
    const endpoint = reportTarget.type === 'question'
      ? `/api/v1/community/questions/${reportTarget.id}/report`
      : `/api/v1/community/answers/${reportTarget.id}/report`;
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_reason: ' inappropriate content' }),
      });
      if (res.ok) {
        flash(t('common.reported'));
      } else {
        const d = await res.json().catch(() => ({}));
        flash(d.detail || t('community.reportFailed'));
      }
    } catch {
      flash(t('community.reportFailed'));
    }
    setReportTarget(null);
  };

  if (loading) return <LoadingScreen />;

  const boxUrl = selfUser ? `/questionbox/@${selfUser.user_id}@${selfUser.server}` : '/';

  const questionFeed = (
    <div className="px-6 py-8 space-y-6">
      {/* Post question form */}
      <Card className="!p-6">
        <div className="flex items-center gap-2 mb-4">
          <QGlyph tone="green" size={28} />
          <h2 className="text-base font-bold">{t('community.postQuestion')}</h2>
        </div>
        {loggedIn ? (
          <>
            <textarea
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('community.postQuestionDesc')}
              className="w-full bg-ink-100 dark:bg-ink-850 rounded-2xl p-4 text-[15px] leading-relaxed resize-none outline-none placeholder:text-ink-400 focus:ring-2 focus:ring-brand-green-300 transition"
            />
            <div className="flex justify-between items-center mt-1">
              <span className={`text-[12px] ${text.length > charMax ? 'text-rose-500' : 'text-ink-400'}`}>
                {text.length} / {charMax}
              </span>
              <Btn
                variant="green"
                size="md"
                onClick={handlePostQuestion}
                disabled={!text.trim() || posting || text.length > charMax}
              >
                <Icons.Sparkle width={14} height={14} />
                {posting ? t('common.posting') : t('community.postButton')}
              </Btn>
            </div>
            <p className="mt-3 text-[11px] text-ink-500 dark:text-ink-400">
              {t('community.postLimit')}
            </p>
          </>
        ) : (
          <div className="py-4 text-center space-y-3">
            <p className="text-[13px] text-ink-500 dark:text-ink-400">
              {t('common.requiredForPost')}
            </p>
            <Btn variant="primary" size="md" onClick={() => navigate('/login')}>
              {t('common.loginAndPost')}
            </Btn>
          </div>
        )}
      </Card>

      {/* Question feed */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[14px] font-bold flex items-center gap-2">
            <Icons.Compass width={16} height={16} className="text-brand-green-500" />
            {t('community.latestQuestions')}
          </h3>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <span className="text-[12px] text-ink-500 dark:text-ink-400">{t('common.hideSensitive')}</span>
            <button
              type="button"
              role="switch"
              aria-checked={hideSensitive}
              onClick={() => setHideSensitive((v) => !v)}
              className={`relative w-9 h-5 rounded-full transition shrink-0 ${
                hideSensitive ? 'bg-brand-green-500' : 'bg-ink-300 dark:bg-ink-700'
              }`}
            >
              <span
                className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${
                  hideSensitive ? 'right-0.5' : 'left-0.5'
                }`}
              />
            </button>
          </label>
        </div>

        {fetching && page === 0 ? (
          <div className="text-center py-12 text-ink-400 flex flex-col items-center gap-3">
            <div className="w-7 h-7 rounded-full border-2 border-ink-200 dark:border-ink-700 border-t-brand-blue-400 animate-spin" />
            <span className="text-[13px]">{t('common.loading')}</span>
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-12 text-ink-400">
            <p className="text-[14px]">{t('community.empty')}</p>
            {loggedIn && <p className="text-[12px] mt-1">{t('community.emptySub')}</p>}
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {questions.filter((q) => !hideSensitive || !q.is_sensitive).map((q) => (
                <Card
                  key={q.id}
                  className="!p-5 hover:border-brand-green-200 dark:hover:border-brand-green-800 transition-colors cursor-pointer"
                  onClick={() => setDetailQ(q)}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/questionbox/@${q.user_id}@${q.server}`); }}
                      className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity text-left"
                    >
                      <Avatar src={q.avatar} seed={q.display_name} size={36} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[14px]">{q.display_name}</div>
                        <div className="text-[11px] text-ink-500 dark:text-ink-400 font-mono truncate">
                          @{q.user_id}@{q.server}
                        </div>
                      </div>
                    </button>
                    <span className="text-[11px] text-ink-400 shrink-0">{UTCtoJST(q.created_at)}</span>
                  </div>
                  <p className="text-[15px] font-semibold leading-snug mb-3 line-clamp-3"><TranslateText text={q.question} inline /></p>
                  <div className="flex items-center gap-3 text-[12px] text-ink-500 dark:text-ink-400">
                    <span className="flex items-center gap-1">
                      <Icons.Reply width={13} height={13} /> {t('common.countAnswers', { count: q.answer_count })}
                    </span>
                    {q.nsfw && <Tag tone="outline" className="!text-rose-500 !border-rose-200">{t('common.nsfw')}</Tag>}
                    {q.is_sensitive && <Tag tone="outline" className="!text-amber-500 !border-amber-200">{t('common.sensitive')}</Tag>}
                  </div>
                </Card>
              ))}
              {hideSensitive && questions.some((q) => q.is_sensitive) && questions.filter((q) => !q.is_sensitive).length === 0 && (
                <div className="text-center py-8 text-ink-400">
                  <p className="text-[13px]">{t('community.hiddenSensitive')}</p>
                  <button
                    onClick={() => setHideSensitive(false)}
                    className="mt-2 text-[12px] text-brand-blue-500 hover:underline"
                  >
                    {t('common.showAllItems')}
                  </button>
                </div>
              )}
            </div>
            {questions.length === 20 && (
              <div className="flex justify-center mt-6">
                <Btn
                  variant="outline"
                  size="md"
                  onClick={() => {
                    const next = page + 1;
                    setPage(next);
                    fetchQuestions(next);
                  }}
                  disabled={fetching}
                >
                  {fetching ? t('common.loading') : t('common.showMore')}
                </Btn>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-ink-50 dark:bg-ink-950 text-ink-800 dark:text-ink-100">
      {ToastEl}

      {loggedIn && selfUser ? (
        /* ログイン済み: サイドバーあり */
        <>
          <div className="flex md:h-screen max-w-[1440px] mx-auto">
            <aside className="hidden md:block w-64 shrink-0 border-r border-ink-150 dark:border-ink-800">
              <Sidebar
                activeKey="community"
                onNavigate={(key) => {
                  if (key === 'community') return;
                  navigate(`/mypage?tab=${key}`);
                }}
                unansweredCount={unansweredCount}
                userInfo={selfUser}
                onGoToBox={() => navigate(boxUrl)}
              />
            </aside>
            <main className="flex-1 overflow-y-auto min-w-0 pb-20 md:pb-0">
              {questionFeed}
            </main>
            <aside className="hidden xl:block w-72 shrink-0 border-l border-ink-150 dark:border-ink-800 overflow-y-auto">
              {selfUser && <RightSidebar userInfo={selfUser} />}
            </aside>
          </div>
          <div className="md:hidden">
            <MobileNav
              activeKey="community"
              onNavigate={(key) => {
                if (key === 'community') return;
                navigate(`/mypage?tab=${key}`);
              }}
              unansweredCount={unansweredCount}
              onGoToBox={() => navigate(boxUrl)}
            />
          </div>
        </>
      ) : (
        /* 未ログイン: シンプルヘッダー */
        <>
          <header className="flex items-center justify-between px-6 py-4 border-b border-ink-150 dark:border-ink-800 bg-white/80 dark:bg-ink-900/80 backdrop-blur-sm sticky top-0 z-10">
            <button onClick={() => navigate('/')} className="shrink-0">
              <img src="/fediqb_banner_dark.svg" alt="FediQB" className="h-6 select-none dark:hidden" />
              <img src="/fediqb_banner.svg" alt="FediQB" className="h-6 select-none hidden dark:block" />
            </button>
            <div className="flex items-center gap-2">
              <Btn variant="ghost" size="sm" onClick={() => navigate('/login')}>
                {t('common.login')}
              </Btn>
            </div>
          </header>
          <main>{questionFeed}</main>
        </>
      )}

      {/* Modals */}
      {detailQ && (
        <QuestionDetailModal
          question={detailQ}
          answers={detailAnswers}
          selfUser={selfUser}
          onClose={() => setDetailQ(null)}
          onAnswered={(q, answers) => {
            setDetailQ(q);
            setDetailAnswers(answers);
          }}
          onDeleteQuestion={handleDeleteQuestion}
          onDeleteAnswer={handleDeleteAnswer}
          onReportQuestion={(id) => setReportTarget({ type: 'question', id })}
          onReportAnswer={(id) => setReportTarget({ type: 'answer', id })}
          onPromote={handlePromote}
          onShare={handleShare}
        />
      )}
      {reportTarget && (
        <ReportModal onClose={() => { handleReport(); }} />
      )}
      {promoteTarget && selfUser && (
        <FediversePromoteModal
          question={promoteTarget}
          selfUser={selfUser}
          onClose={() => setPromoteTarget(null)}
          onPosted={() => flash(t('community.promoteDone'))}
        />
      )}
      {shareTarget && selfUser && (
        <FediversePromoteModal
          question={shareTarget}
          selfUser={selfUser}
          onClose={() => setShareTarget(null)}
          onPosted={() => flash(t('community.shareDone'))}
          mode="share"
        />
      )}
    </div>
  );
};

export default CommunityPage;
